import type { ComponentType } from 'react'
import { Fragment, useCallback, useRef } from 'react'

import debounce from 'lodash/debounce'
import type { ListChildComponentProps, ListOnScrollProps } from 'react-window'
import { VariableSizeList } from 'react-window'

import { IconLoading } from '@/assets/icons/icon-loading'
import NotFoundImage from '@/assets/images/not-found.svg'
import { Tooltip } from '@/components/ui/tooltip'
import ChevronDownIcon from '@/modules/chatting/icons/chevron-down.svg'
import TooltipIcon from '@/modules/chatting/icons/tooltip.svg'
import { useSizeObserver } from '@/modules/chatting/ui/sidebar/use-size-observer'
import { classNames } from '@/utils/helpers'

import { ListItem } from './ListItem'
import { ListItemSkeleton } from './ListItemSkeleton'
import type { CollectionsItem } from './types'

interface Props {
  lists: CollectionsItem[]
  isLoading: boolean
  canLoadMore: boolean
  onLoadMore: () => void
  isFetchingNextPage: boolean
  onSelectListId: (listId: CollectionsItem['id']) => void
  isCustomizeOrderModeActive: boolean
  onReorder: (origin: CollectionsItem, target: CollectionsItem) => void
  onToggleDefaultListsVisibility: () => void
  isDefaultListsVisible: boolean
}

export const ListContent = ({
  lists,
  isLoading,
  canLoadMore,
  onLoadMore,
  isFetchingNextPage,
  onSelectListId,
  isCustomizeOrderModeActive,
  onReorder,
  isDefaultListsVisible,
  onToggleDefaultListsVisibility,
}: Props) => {
  const lastVisibleIndexRef = useRef<number | null>(null)
  const [parentRef, width, height] = useSizeObserver()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleScroll = useCallback(
    debounce(({ scrollDirection }: ListOnScrollProps) => {
      const lastItemIndex = lastVisibleIndexRef.current

      if (
        lastItemIndex === null ||
        lastItemIndex !== lists.length - 1 ||
        !canLoadMore
      ) {
        return
      }

      if (scrollDirection === 'forward') {
        onLoadMore()
      }
    }, 100),
    [onLoadMore, canLoadMore, lists.length],
  )

  const getItemSize = (index: number) => {
    const list = lists[index]

    if (isCustomizeOrderModeActive && list.type !== 'custom') {
      let rowHeight = 0

      if (index === 0) {
        rowHeight += 50
      }
      if (isDefaultListsVisible) {
        rowHeight += 83
      }

      return rowHeight
    }

    return 83
  }

  const renderListItem: ComponentType<
    ListChildComponentProps<CollectionsItem[]>
  > = useCallback(
    ({ data, index, style }) => {
      const list = data[index]

      lastVisibleIndexRef.current = index

      return (
        <div
          style={style}
          className="overflow-hidden"
        >
          {isCustomizeOrderModeActive && index === 0 && (
            <div
              className="mb-2 flex cursor-pointer items-center justify-between rounded-lg px-3 py-2 hover:bg-primary-50 dark:hover:bg-primary-900"
              onClick={onToggleDefaultListsVisibility}
            >
              <div className="flex items-center gap-1">
                <p className="font-semibold leading-[26px]">Default lists</p>
                <Tooltip
                  content="Default OnlyFans lists cannot be reordered"
                  contentClassName="max-w-[300px]"
                >
                  <TooltipIcon className="h-4 w-4 text-secondary-300" />
                </Tooltip>
              </div>
              <ChevronDownIcon
                className={classNames({ 'rotate-180': isDefaultListsVisible })}
              />
            </div>
          )}
          <ListItem
            onMove={onReorder}
            list={list}
            onClick={onSelectListId}
            isCustomizeOrderModeActive={isCustomizeOrderModeActive}
          />
        </div>
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onReorder, isCustomizeOrderModeActive, isDefaultListsVisible],
  )

  const noListsView = (
    <div className="flex h-full flex-col items-center justify-center space-y-4">
      <NotFoundImage
        width={80}
        height={80}
      />
      <div className="text-sm leading-6 text-secondary-300">
        No user lists yet
      </div>
    </div>
  )

  const loadingView = (
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={index}>
          <ListItemSkeleton />
          <div className="border-b" />
        </Fragment>
      ))}
    </div>
  )

  if (!lists.length && !isLoading) {
    return noListsView
  }

  return (
    <div
      className="no-scrollbar relative max-w-full overflow-hidden rounded-lg border p-4 border-border-secondary"
      ref={parentRef}
    >
      {isLoading ? (
        loadingView
      ) : (
        <div className="relative min-h-[490px] w-full max-w-full overflow-hidden">
          <VariableSizeList
            key={
              String(isCustomizeOrderModeActive) + String(isDefaultListsVisible)
            }
            onScroll={handleScroll}
            className="scrollbar-hide"
            itemData={lists}
            itemSize={getItemSize}
            height={height}
            itemCount={lists?.length ?? 0}
            width={width}
          >
            {renderListItem}
          </VariableSizeList>
        </div>
      )}
      {isFetchingNextPage && (
        <div className="absolute bottom-0 left-0 right-0 bg-card py-3">
          <IconLoading className="mx-auto animate-spin" />
        </div>
      )}
    </div>
  )
}
